import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../utils/axios';
import TeamMembersTable from './TeamMembersTable';
import InvitationsTable from './InvitationsTable';
import { getInvitationState } from './dashboardUtils';
import './dashboard.css';

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [copiedStates, setCopiedStates] = useState({}); // Track copy feedback
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'user',
    tenant_code: ''
  });
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const pendingCount = invitations.filter(inv => getInvitationState(inv) === 'pending').length;

  useEffect(() => {
    fetchUserData();
  }, [navigate]);

  const fetchUserData = async () => {
    try {
      // Fetch current user info
      const userResponse = await apiClient.get('/iam/me');

      const userData = userResponse.data;
      setUser(userData);
      setInviteForm(prev => ({
        ...prev,
        tenant_code: userData.tenant_code
      }));

      // Fetch users in tenant
      const usersResponse = await apiClient.get('/iam/users');
      setUsers(usersResponse.data);

      // Fetch invitations (admins only)
      if (userData.role === 'admin') {
        const invitationsResponse = await apiClient.get('/iam/invitations');
        setInvitations(invitationsResponse.data);
      }

    } catch (err) {
      console.error(err);
      setError('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  // Refresh lists after a mutation without toggling the page-level loading state
  const refetchLists = async () => {
    try {
      const usersResponse = await apiClient.get('/iam/users');
      setUsers(usersResponse.data);
      if (isAdmin) {
        const invitationsResponse = await apiClient.get('/iam/invitations');
        setInvitations(invitationsResponse.data);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to refresh data');
    }
  };

  // Runs a mutation, surfaces backend errors in the banner, refetches on success.
  // Rethrows so table components can reset their pending state.
  const detailOr = (err, fallback) => {
    const detail = err.response?.data?.detail;
    return typeof detail === 'string' && detail ? detail : fallback;
  };

  const mutate = async (fn, fallback) => {
    setError('');
    try {
      await fn();
    } catch (err) {
      setError(detailOr(err, fallback));
      throw err;
    }
    await refetchLists();
  };

  const handleChangeRole = (id, role) =>
    mutate(() => apiClient.put(`/iam/users/${id}/role`, null, { params: { role } }), 'Failed to change role');
  const handleSetActive = (id, isActive) =>
    mutate(() => apiClient.put(`/iam/users/${id}/status`, { is_active: isActive }), 'Failed to update user status');
  const handleRevoke = (id) =>
    mutate(() => apiClient.post(`/iam/invitations/${id}/revoke`), 'Failed to revoke invitation');
  const handleRenew = (id) =>
    mutate(() => apiClient.post(`/iam/invitations/${id}/renew`), 'Failed to renew invitation');

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await apiClient.post('/iam/invitations', inviteForm);
      
      setShowInviteForm(false);
      setInviteForm({
        email: '',
        role: 'user',
        tenant_code: user.tenant_code
      });
      await refetchLists();
    } catch (err) {
      console.error(err);
      if (err.response && err.response.data) {
        setError(detailOr(err, 'Failed to send invitation'));
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = async (text, type, invitationId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({
        ...prev,
        [`${invitationId}-${type}`]: true
      }));
      
      // Reset copy feedback after 2 seconds
      setTimeout(() => {
        setCopiedStates(prev => ({
          ...prev,
          [`${invitationId}-${type}`]: false
        }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      setError('Failed to copy to clipboard');
    }
  };

  const generateInvitationLink = (invitationId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/invitation?invitation=${invitationId}`;
  };

  const generateInvitationMessage = (invitation) => {
    const invitationLink = generateInvitationLink(invitation.invitation_id);
    const expiryDate = new Date(invitation.expires_at).toLocaleDateString();
    
    return `Hi there!

You've been invited to join TouchFlow as a ${invitation.role}.

Please click the link below to create your account:
${invitationLink}

This invitation will expire on ${expiryDate}.

Welcome to the team!`;
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="dashboard-container">
        <div className="error">Failed to load user data</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>TouchFlow Dashboard</h1>
          <div className="user-info">
            <span>Welcome, {user.first_name || user.email}</span>
          </div>
        </div>
      </header>

      <main className="dashboard-main">
        {error && (
          <div className="error-message">
            <span>{error}</span>
            <button onClick={() => setError('')} className="close-error">×</button>
          </div>
        )}

        <div className="dashboard-grid">
          {/* Stats */}
          <div className="stats-grid">
            <div className="stat-item profile-tile">
              <span className="stat-label">Your Profile</span>
              <strong className="profile-name">
                {[user.first_name, user.last_name].filter(Boolean).join(' ') || user.email}
              </strong>
              <span className="profile-email">{user.email}</span>
              <div className="profile-meta">
                <span className="profile-chip">{user.role}</span>
                <span className="profile-chip">{user.tenant_code}</span>
                <span className={`status ${user.is_active ? 'active' : 'inactive'}`}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <span className="profile-created">
                Created {new Date(user.created_at).toLocaleDateString()}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{users.length}</span>
              <span className="stat-label">Total Users</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{users.filter(u => u.is_active).length}</span>
              <span className="stat-label">Active Users</span>
            </div>
            {isAdmin && (
              <div className="stat-item">
                <span className="stat-number">{pendingCount}</span>
                <span className="stat-label">Pending Invites</span>
              </div>
            )}
          </div>

          {/* Invitations */}
          {isAdmin && invitations.length > 0 && (
            <InvitationsTable
              invitations={invitations}
              copiedKey={Object.keys(copiedStates).find(k => copiedStates[k])}
              onCopyLink={(inv) => copyToClipboard(
                generateInvitationLink(inv.invitation_id),
                'link',
                inv.invitation_id
              )}
              onCopyMessage={(inv) => copyToClipboard(
                generateInvitationMessage(inv),
                'message',
                inv.invitation_id
              )}
              onRenew={handleRenew}
              onRevoke={handleRevoke}
            />
          )}
          {isAdmin && invitations.length === 0 && (
            <p className="no-data no-invitations">No pending invitations</p>
          )}

          {/* Team Members */}
          <TeamMembersTable
            users={users}
            currentUser={user}
            isAdmin={isAdmin}
            onInvite={() => setShowInviteForm(true)}
            onChangeRole={handleChangeRole}
            onSetActive={handleSetActive}
          />
        </div>
      </main>

      {/* Invite Form Modal */}
      {showInviteForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Invite New User</h3>
              <button 
                onClick={() => setShowInviteForm(false)}
                className="close-modal"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleInviteSubmit} className="invite-form">
              <div className="form-group">
                <label htmlFor="tenant_code">Workspace Code</label>
                <input
                  type="text"
                  id="tenant_code"
                  value={inviteForm.tenant_code}
                  onChange={(e) => setInviteForm({...inviteForm, tenant_code: e.target.value})}
                  placeholder="Enter workspace code"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                  placeholder="Enter email address"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="role">Role</label>
                <select
                  id="role"
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({...inviteForm, role: e.target.value})}
                  required
                >
                  <option value="user">User</option>
                  {user.role === 'admin' && <option value="admin">Admin</option>}
                </select>
              </div>
              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={() => setShowInviteForm(false)}
                  className="secondary-btn"
                >
                  Cancel
                </button>
                <button type="submit" className="primary-btn" disabled={submitting}>
                  Send Invitation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 